import React, { useEffect, useRef, useState } from "react";
import { X, Megaphone } from "lucide-react";

const FIRST_DELAY_MS = 5 * 60 * 1000; // 5 minutes after mount
const INTERVAL_MS = 5 * 60 * 1000; // every 5 minutes after close
const MANDATORY_SECONDS = 15;

export const AdPopup = () => {
  const [open, setOpen] = useState(false);
  const [remaining, setRemaining] = useState(MANDATORY_SECONDS);
  const openTimerRef = useRef(null);
  const tickRef = useRef(null);

  const scheduleOpen = (delay) => {
    clearTimeout(openTimerRef.current);
    openTimerRef.current = setTimeout(() => {
      setOpen(true);
      setRemaining(MANDATORY_SECONDS);
    }, delay);
  };

  useEffect(() => {
    scheduleOpen(FIRST_DELAY_MS);
    return () => {
      clearTimeout(openTimerRef.current);
      clearInterval(tickRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setRemaining((r) => (r > 0 ? r - 1 : 0));
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [open]);

  const handleClose = () => {
    if (remaining > 0) return;
    setOpen(false);
    scheduleOpen(INTERVAL_MS);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="ad-popup-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Sponsored advertisement"
    >
      <div
        className="relative w-full max-w-md h-[620px] bg-[#0A0A0A] border-4 border-[#FFDE00] flex flex-col"
        style={{ boxShadow: "16px 16px 0 0 #FF2E9A" }}
        data-testid="ad-popup-modal"
      >
        <div className="absolute top-2 left-2 fb-tape text-xs z-10">SPONSORED</div>

        {/* Timer / Close cluster */}
        <div className="absolute -top-7 -right-7 z-20">
          {remaining > 0 ? (
            <div
              className="w-16 h-16 bg-[#FF2E9A] text-white rounded-full border-4 border-white flex items-center justify-center font-heading text-xl"
              data-testid="ad-popup-timer"
              aria-live="polite"
            >
              {remaining}
            </div>
          ) : (
            <button
              type="button"
              onClick={handleClose}
              className="w-16 h-16 bg-white text-black rounded-full border-4 border-black flex items-center justify-center hover:bg-[#FFDE00] cursor-pointer"
              data-testid="ad-popup-close"
              aria-label="Close advertisement"
            >
              <X strokeWidth={4} size={30} />
            </button>
          )}
        </div>

        {/* Ad content placeholder */}
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
          <Megaphone size={80} strokeWidth={3} color="#FFDE00" />
          <div className="font-heading text-4xl md:text-5xl text-white mt-6 leading-none">
            YOUR AD
            <br />
            <span className="text-[#FF2E9A]">RIGHT HERE</span>
          </div>
          <div className="font-graffiti text-[#FFDE00] text-2xl mt-6">
            vertical · 400×600
          </div>
          <p className="font-body text-sm md:text-base uppercase tracking-widest text-white/60 mt-6 max-w-xs">
            This ad is served every 5 minutes. Closes after the timer.
          </p>
        </div>

        <div className="border-t-4 border-[#FFDE00] bg-black px-4 py-3 text-center">
          <span className="font-graffiti text-[#FF2E9A] text-lg">
            FATBOY · stay thick
          </span>
        </div>
      </div>
    </div>
  );
};
